import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";

export default function QRReviewCard() {
  const [settings, setSettings] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/settings/admin").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const reviewUrl = settings?.google_business_url || "";
  const qrUrl = reviewUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&ecc=H&color=1A1918&bgcolor=FDFBF7&data=${encodeURIComponent(reviewUrl)}`
    : "";

  const handlePrint = () => {
    if (!cardRef.current) return;
    const html = cardRef.current.outerHTML;
    const doc = `<!doctype html><html><head><title>Westbridge — Leave a Review</title>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@600;700;800&family=Manrope:wght@400;500;600&display=swap" rel="stylesheet">
      <style>
        body { margin: 0; background: #FDFBF7; font-family: Manrope, sans-serif; }
        .print-card { width: 5.5in; height: 8.5in; margin: 0.25in auto; }
        @media print { body { margin: 0; } @page { size: letter; margin: 0.4in; } }
      </style></head><body>${html}<script>window.onload=()=>setTimeout(()=>window.print(),400);</${"script"}></body></html>`;
    const blob = new Blob([doc], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=900,height=1200");
    if (w) setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  if (!settings) return <p className="text-ink-muted">Loading…</p>;

  return (
    <div data-testid="qr-review-card-panel">
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <h2 className="font-display text-3xl text-ink">QR Review Card</h2>
          <p className="text-ink-muted mt-2 max-w-2xl">
            Print this card and place it at the lobby front desk, breakfast area, or as a tent card in
            each room. Guests scan, land on your Google review page, and leave a review in under 30
            seconds. Hotels that do this typically see a 3–5× boost in review velocity.
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handlePrint} className="btn-ember !rounded-none" data-testid="qr-print-btn">
            Print card
          </Button>
          <a href={qrUrl} download="westbridge-qr.png" className="btn-ink" data-testid="qr-download-btn">
            Download QR (PNG)
          </a>
        </div>
      </div>

      {!reviewUrl && (
        <div className="bg-yellow-100 border-l-4 border-yellow-600 p-4 mb-6">
          <p className="text-ink-soft">
            Add your Google Business profile URL in <strong>Local SEO → Google Business Profile</strong> first.
          </p>
        </div>
      )}

      <div className="flex justify-center bg-dune/40 p-8">
        {/* Printable card */}
        <div
          ref={cardRef}
          className="print-card bg-canvas relative"
          style={{ width: "5.5in", height: "8.5in", padding: "0.6in", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}
          data-testid="qr-card"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <img src={LOGO_URL} alt="Westbridge logo" style={{ width: "56px", height: "56px", borderRadius: 2 }} />
            <div>
              <p style={{ fontFamily: "Nunito, sans-serif", fontSize: 28, color: "#1A1918", lineHeight: 1, margin: 0 }}>Westbridge</p>
              <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7A7671", margin: "4px 0 0" }}>Inn &amp; Suites</p>
            </div>
          </div>

          <div style={{ marginTop: "48px" }}>
            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#E85D22", margin: 0 }}>
              Scan to leave a review
            </p>
            <h1 style={{ fontFamily: "Nunito, sans-serif", fontSize: 56, color: "#1A1918", lineHeight: 1, margin: "16px 0 0", letterSpacing: "-0.02em" }}>
              Loved your stay?
            </h1>
            <p style={{ fontFamily: "Nunito, sans-serif", fontStyle: "italic", fontSize: 36, color: "#E85D22", margin: "8px 0 0", lineHeight: 1.05 }}>
              Tell the world.
            </p>
          </div>

          {qrUrl && (
            <div style={{ marginTop: "44px", display: "flex", justifyContent: "center" }}>
              <img src={qrUrl} alt="QR code to leave a Google review" style={{ width: "3in", height: "3in", border: "1px solid #EAE5DB" }} />
            </div>
          )}

          <div style={{ marginTop: "44px", textAlign: "center" }}>
            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#4A4846", margin: 0, lineHeight: 1.5 }}>
              Open your phone's camera. Point it at the square above.<br />
              Tap the link — it goes straight to our Google page.<br />
              A few honest sentences would mean the world to us.
            </p>
          </div>

          <div style={{ position: "absolute", bottom: "0.5in", left: "0.6in", right: "0.6in", paddingTop: 16, borderTop: "1px solid #EAE5DB", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7A7671", margin: 0 }}>
              {settings.nap_phone}
            </p>
            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7A7671", margin: 0 }}>
              westbridgecarrollton.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
